import apiClient from './apiClient';

export interface NewsUpdate {
  title: string;
  content: string;
  'google-search-keywords'?: string;
  source?: string;
  published_date?: string;
}

export interface SkillNewsItem {
  skill: string;
  latest_updates: NewsUpdate[];
}

export interface SkillNewsResponse {
  news: SkillNewsItem[];
  totalSkills?: number;
  generatedAt?: string;
  professionalSummary?: string;
}

export interface GenerateSkillNewsOptions {
  skillCount?: number;
  newsPerSkill?: number;
  forceRegenerate?: boolean;
}

export interface StreamCallbacks {
  onOpen?: () => void;
  onClose?: () => void;
  onMessage?: (message: string, eventType: string) => void; // eventType can be 'status', 'cached', 'complete', 'error'
  onData?: (data: SkillNewsResponse, eventType: string) => void; // For 'cached' or 'complete'
  onError?: (error: Event | string) => void;
}

export interface SkillNewsJobStartResponse {
  jobId: string;
  status: 'pending' | 'complete' | 'error';
  result?: SkillNewsResponse;
  error?: string;
}

export interface SkillNewsJobStatusResponse {
  jobId: string;
  status: 'pending' | 'complete' | 'error';
  result?: SkillNewsResponse;
  error?: string;
}

export interface SkillNewsUsageResponse {
  year: number;
  month: number;
  usageCount: number;
  usedDates: string[];
  monthlyLimit: number;
  isPremium: boolean;
  remainingUsage: number;
  canUseToday: boolean;
  hasUsedToday: boolean;
}

export interface SkillNewsAccessCheck {
  canGenerate: boolean;
  reason?: string;
  usage?: SkillNewsUsageResponse;
  isPremium: boolean;
}

class SkillNewsService {
  /**
   * Get skill-based news for the current user
   */
  async getSkillNews(options: GenerateSkillNewsOptions = {}): Promise<SkillNewsResponse> {
    try {
      const params = new URLSearchParams();

      if (options.skillCount) {
        params.append('skillCount', options.skillCount.toString());
      }
      if (options.newsPerSkill) {
        params.append('newsPerSkill', options.newsPerSkill.toString());
      }
      if (options.forceRegenerate) {
        params.append('forceRegenerate', options.forceRegenerate.toString());
      }

      const response = await apiClient.get<SkillNewsResponse>(
        `/feed/skill-news?${params.toString()}`,
        {},
        true
      );

      return response;
    } catch (error) {
      console.error('Error fetching skill news:', error);
      throw error;
    }
  }

  /**
   * Generate new skill-based news
   */
  async generateSkillNews(options: GenerateSkillNewsOptions = {}): Promise<SkillNewsResponse> {
    try {
      const response = await apiClient.post<SkillNewsResponse>(
        '/feed/skill-news/generate',
        options,
        {},
        true
      );

      return response;
    } catch (error) {
      console.error('Error generating skill news:', error);
      throw error;
    }
  }

  /**
   * Start async skill news generation job (returns jobId and initial status)
   */
  async startAsyncNewsJob(options: GenerateSkillNewsOptions = {}): Promise<SkillNewsJobStartResponse> {
    try {
      const response = await apiClient.post<SkillNewsJobStartResponse>(
        '/feed/skill-news/generate-async',
        options,
        {},
        true
      );
      return response;
    } catch (error) {
      this.logError('Error starting async skill news job:', error);
      throw error;
    }
  }

  /**
   * Poll async skill news job status/result by jobId
   */
  async getAsyncNewsJobStatus(jobId: string): Promise<SkillNewsJobStatusResponse> {
    try {
      const response = await apiClient.get<SkillNewsJobStatusResponse>(
        `/feed/skill-news/job-status/${jobId}`,
        {},
        true
      );
      return response;
    } catch (error) {
      this.logError('Error polling async skill news job status:', error);
      throw error;
    }
  }

  /**
   * Poll async news job until complete or error, with interval and timeout.
   * Resolves with result or rejects on error/timeout.
   */
  async pollAsyncNewsJob(
    jobId: string,
    { intervalMs = 2000, timeoutMs = 600000 }: { intervalMs?: number; timeoutMs?: number } = {} // 10 minutes default
  ): Promise<SkillNewsResponse> {
    const start = Date.now();
    while (true) {
      const status = await this.getAsyncNewsJobStatus(jobId);
      if (status.status === 'complete' && status.result) {
        return status.result;
      }
      if (status.status === 'error') {
        throw new Error(status.error || 'Skill news job failed');
      }
      if (Date.now() - start > timeoutMs) {
        throw new Error('Skill news job timed out');
      }
      await new Promise(res => setTimeout(res, intervalMs));
    }
  }

  /**
   * Clear skill news cache
   */
  async clearCache(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        '/feed/skill-news/clear-cache',
        {},
        true
      );

      return response;
    } catch (error) {
      console.error('Error clearing skill news cache:', error);
      throw error;
    }
  }

  /**
   * Get current month skill news usage and access check
   */
  async getCurrentMonthUsage(): Promise<SkillNewsUsageResponse> {
    try {
      const response = await apiClient.get<{
        canGenerate: boolean;
        reason?: string;
        usage: SkillNewsUsageResponse;
        isPremium: boolean;
      }>('/feed/skill-news/usage', {}, true);
      
      return response.usage;
    } catch (error) {
      this.logError('Error fetching current month usage:', error);
      throw error;
    }
  }

  /**
   * Check if user can generate skill news and get usage data
   */
  async checkAccess(): Promise<SkillNewsAccessCheck> {
    try {
      const response = await apiClient.get<{
        canGenerate: boolean;
        reason?: string;
        usage: SkillNewsUsageResponse;
        isPremium: boolean;
      }>('/feed/skill-news/usage', {}, true);

      return {
        canGenerate: response.canGenerate,
        reason: response.reason,
        usage: response.usage,
        isPremium: response.isPremium
      };
    } catch (error) {
      this.logError('Error checking skill news access:', error);
      throw error;
    }
  }

  // Helper logging methods (optional, but good for debugging)
  private logInfo(message: string, ...optionalParams: any[]) {
    console.log(`[SkillNewsService] INFO: ${message}`, ...optionalParams);
  }

  private logError(message: string, ...optionalParams: any[]) {
    console.error(`[SkillNewsService] ERROR: ${message}`, ...optionalParams);
  }
}

const skillNewsService = new SkillNewsService();
export default skillNewsService;